import { useMutation, useReactiveVar } from '@apollo/client';
import { AnalyticIdentifier, createError } from '@common/neo.tax';
import * as Sentry from '@sentry/react';
import {
  Button,
  colors,
  FileDisplaySection,
  FileDropZone,
  FileMimeType,
  InputFormContainer,
  ModalActions,
  ModalHeader,
  Stack,
} from '@ui-components/neo.tax';
import { ReactComponent as PlusCircle } from '@ui-components/neo.tax/dist/icons/plus-circle.svg';
import { SyntheticEvent, useMemo, useState } from 'react';

import {
  AddPriorYearTaxDocumentMutation,
  AddPriorYearTaxDocumentMutationVariables,
  ADD_PRIOR_YEAR_TAX_DOCUMENT,
  FileUploadsState,
  GetPriorYearTaxDocumentSignedUploadUrlMutation,
  GetPriorYearTaxDocumentSignedUploadUrlMutationVariables,
  GET_PRIOR_YEAR_TAX_DOCUMENT_SIGNED_UPLOAD_URL,
  initiateResumableFileUpload,
  ResumableFileUpload,
  useAnalyticId,
  useCurrentCompanyOrFail,
} from '@neo.tax/shared';

import { StyledModal } from './prior-year-tax-document-upload-modal.styled';

type PriorYearTaxDocumentsModalProps = {
  isOpen: boolean;
  close: () => void;
};

const PRIOR_YEAR_TAX_DOCUMENT_TAG = 'PRIOR_YEAR_TAX_DOCUMENT_TAG';

export const PriorYearTaxDocumentsModal = ({ close, isOpen }: PriorYearTaxDocumentsModalProps): React.ReactElement => {
  const priorYearTaxDocumentUploadId = useAnalyticId(AnalyticIdentifier.PRIOR_YEAR_TAX_DOCUMENT_UPLOAD);
  const company = useCurrentCompanyOrFail();

  // including the company ID allows this to continue working even when you switch companies
  const priorYearTaxDocumentUploadTag = `${PRIOR_YEAR_TAX_DOCUMENT_TAG}-${company.id}`;

  // Using this state to get a more responsive UI when the upload button is clicked
  const [disableUploadButton, setDisableUploadButton] = useState(false);

  const [getUploadUrlMutation] = useMutation<
    GetPriorYearTaxDocumentSignedUploadUrlMutation,
    GetPriorYearTaxDocumentSignedUploadUrlMutationVariables
  >(GET_PRIOR_YEAR_TAX_DOCUMENT_SIGNED_UPLOAD_URL);

  const [addPriorYearTaxDocument, { loading: loadingAddPriorYearTaxDocument }] = useMutation<
    AddPriorYearTaxDocumentMutation,
    AddPriorYearTaxDocumentMutationVariables
  >(ADD_PRIOR_YEAR_TAX_DOCUMENT);

  useReactiveVar(FileUploadsState.fileUploadsVar); // subscribe re-render on file upload state change

  const addFiles = async (files: File[]): Promise<void> => {
    await FileUploadsState.addManyFileUploads(files, priorYearTaxDocumentUploadTag);
  };

  const triggerClientFileUpload = async (fileUpload: ResumableFileUpload): Promise<void> => {
    await initiateResumableFileUpload({
      fileUpload,
      getUploadUrl: async () => {
        // get a signed url from our backend to upload the file to
        const uploadUrlResult = await getUploadUrlMutation({
          variables: { fileName: fileUpload.file.name, companyId: company.id },
        });
        const { permanentUrl, signedUrl } = uploadUrlResult.data?.getPriorYearTaxDocumentSignedUploadUrl ?? {};
        if (!signedUrl || !permanentUrl || uploadUrlResult.errors) {
          Sentry.addBreadcrumb({ level: 'error', data: { uploadUrlResult } });
          throw createError('FileUploadError', 'An unexpected error happened while fetching the signed url.');
        }
        return { permanentUrl, signedUrl };
      },
      onFinishedUploading: async (permanentUrl) => {
        await addPriorYearTaxDocument({
          variables: {
            companyId: company.id,
            input: { permanentUrl },
          },
          optimisticResponse: {
            addPriorYearTaxDocument: {
              __typename: 'Company',
              id: company.id,
              bulkPriorYearTaxDocumentUploads: [
                ...company.bulkPriorYearTaxDocumentUploads,
                {
                  __typename: 'FileUpload',
                  id: 'temp-id',
                  createdAt: new Date().toISOString(),
                  fileName: fileUpload.file.name,
                  signedUrl: 'temp-url',
                },
              ],
            },
          },
        });
      },
    });
  };

  const onSubmit = async (e: SyntheticEvent): Promise<void> => {
    e.preventDefault();
    if (isUploading) {
      return;
    }
    setDisableUploadButton(true);
    const fileUploads = FileUploadsState.getFileUploads(priorYearTaxDocumentUploadTag);
    await Promise.all(fileUploads.map((fileUpload) => triggerClientFileUpload(fileUpload)));
    setDisableUploadButton(false);
    close();
  };

  const filesForUpload = FileUploadsState.getFilesWithUploadProgressForDisplaySection(priorYearTaxDocumentUploadTag);
  const isUploading = loadingAddPriorYearTaxDocument || FileUploadsState.isUploading(priorYearTaxDocumentUploadTag);
  const hasFileErrors = useMemo(() => filesForUpload.some((file) => file.status === 'error'), [filesForUpload]);

  return (
    <StyledModal isOpen={isOpen}>
      <Stack as="form" direction="column" onSubmit={onSubmit}>
        <Stack direction="column" gap={8}>
          <ModalHeader
            icon={<PlusCircle width={48} color={colors.blue[600]} />}
            iconBgColor={colors.white}
            title="Upload Prior Year Tax Documents"
          />

          <InputFormContainer label="Prior Year Tax Documents" inputId={priorYearTaxDocumentUploadId}>
            <FileDropZone
              allowedMimeTypes={[FileMimeType.PDF, FileMimeType.WORD, FileMimeType.EXCEL]}
              addFiles={addFiles}
              text="tax documents"
              allowMultipleSelect
              disable={disableUploadButton}
            />
            <FileDisplaySection files={filesForUpload} removeFile={FileUploadsState.removeFileUpload} />
          </InputFormContainer>
        </Stack>
        <ModalActions>
          <Button variant="white" rounded="regular" size="base" onClick={close}>
            Cancel
          </Button>
          <Button
            variant="primary"
            rounded="regular"
            size="base"
            type="submit"
            disabled={filesForUpload.length === 0 || disableUploadButton || hasFileErrors}
          >
            Upload
          </Button>
        </ModalActions>
      </Stack>
    </StyledModal>
  );
};
